import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Mail, MailX, Power, Loader2, Send, Eye, Shield } from "lucide-react";
import { AdminCustomSendTool } from "./AdminCustomSendTool";
import { SenderVerificationBadge } from "./SenderVerificationBadge";

/**
 * Admin · Communications tab.
 *
 * Central switch for every automated email the platform sends (welcome,
 * trade acknowledgement, order confirmations, admin notifications, etc.).
 * Also lets the admin preview + fire a test send of either elite template.
 *
 * Critical emails (OTP, password reset) bypass this switch server-side via
 * `force=True` so a paused mailer never locks users out.
 */
export function AdminCommunications({ api }) {
  const [enabled, setEnabled] = useState(true);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testTemplate, setTestTemplate] = useState("welcome");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/communications");
      setEnabled(!!r.data.enabled);
      setMeta({ updated_at: r.data.updated_at, updated_by: r.data.updated_by });
    } catch { toast.error("Could not load communications settings"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    const next = !enabled;
    if (!next) {
      const ok = window.confirm(
        "Pause ALL automated emails?\n\n" +
        "This will silence: welcome emails, trade acknowledgements, order confirmations, quotation notices, admin alerts. " +
        "Critical flows (OTP, password reset) still deliver."
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const r = await api.put("/admin/communications", { enabled: next });
      setEnabled(!!r.data.enabled);
      toast.success(next ? "Automated emails resumed" : "Automated emails paused");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testEmail || !testEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    setSending(true);
    try {
      const r = await api.post("/admin/communications/test-email", { to: testEmail, template: testTemplate });
      toast.success(`Test '${testTemplate}' sent to ${r.data.sent_to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Test send failed"); }
    finally { setSending(false); }
  };

  if (loading) return <div className="p-8 text-center font-body text-sm text-[#8A8A82]">Loading…</div>;

  return (
    <div data-testid="admin-communications" className="space-y-6 max-w-3xl">
      <div>
        <h3 className="font-serif text-2xl text-[#F5F5F0]">Communications</h3>
        <p className="font-body text-sm text-[#8A8A82] mt-1">
          Global switch for every automated email leaving the platform. Critical flows (OTP,
          password reset) always deliver — they bypass this toggle.
        </p>
      </div>

      {/* Master toggle card */}
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          data-testid="communications-master-card"
          className={`border rounded-sm p-6 ${enabled ? "border-[#2FE4C6]/40 bg-[#2FE4C6]/[0.05]" : "border-[#E5484D]/40 bg-[#E5484D]/[0.05]"}`}>
        <div className="flex items-start gap-4">
          <div className={`h-12 w-12 rounded-sm flex items-center justify-center ${enabled ? "bg-[#2FE4C6]/15 text-[#2FE4C6]" : "bg-[#E5484D]/15 text-[#E5484D]"}`}>
            {enabled ? <Mail size={22}/> : <MailX size={22}/>}
          </div>
          <div className="flex-1">
            <div className={`font-grotesk text-[10px] uppercase tracking-[0.22em] ${enabled ? "text-[#2FE4C6]" : "text-[#E5484D]"}`}>
              {enabled ? "Automated emails · LIVE" : "Automated emails · PAUSED"}
            </div>
            <div className="font-serif text-2xl text-[#F5F5F0] mt-1">
              {enabled ? "Every send is delivering" : "Every send is suppressed"}
            </div>
            <div className="font-body text-xs text-[#8A8A82] mt-2">
              Applies to welcome emails, trade acknowledgements (RFQ + Order), order/quotation
              status notes, KYC reminders, and admin alerts.
            </div>
            {meta.updated_at && (
              <div className="font-body text-[11px] text-[#6f6f68] mt-2">
                Last change: {new Date(meta.updated_at).toLocaleString("en-IN")}
              </div>
            )}
          </div>
          <button data-testid="communications-master-toggle" onClick={toggle} disabled={saving}
                  className={`shrink-0 font-grotesk text-[10px] uppercase tracking-[0.24em] px-5 py-3 rounded-sm inline-flex items-center gap-2 disabled:opacity-50 ${
                    enabled
                      ? "border border-[#E5484D]/50 text-[#E5484D] hover:bg-[#E5484D]/10"
                      : "bg-[#2FE4C6] hover:bg-[#28cbb1] text-black"
                  }`}>
            {saving ? <Loader2 size={12} className="animate-spin"/> : <Power size={12}/>}
            {enabled ? "Pause emails" : "Resume emails"}
          </button>
        </div>
      </motion.div>

      {/* Template inventory */}
      <div className="grid md:grid-cols-2 gap-4">
        {[
          { id: "welcome",             accent: "#2FE4C6", title: "A · Welcome to the Network",             copy: "Exclusivity & network. Fires on new signup.",                              triggers: "Buyer signup · Seller signup" },
          { id: "trade_ack",           accent: "#D8B23E", title: "B · Trade Executed",                     copy: "Accuracy & operations. Fires on RFQ + Order submission.",                   triggers: "POST /quote-requests · POST /checkout" },
          { id: "sponsorship_glory",   accent: "#D8B23E", title: "C · Sponsorship Glory",                  copy: "Glorifies the sponsor; makes the buyer feel elite.",                        triggers: "Sponsor–buyer link saved" },
          { id: "ekyc_success",        accent: "#2FE4C6", title: "D · eKYC · Verified",                    copy: "Confirms KYC/eKYC success; opens the ledger.",                              triggers: "kyc_status → verified" },
          { id: "certificate_procure", accent: "#D8B23E", title: "E · Procure Certificate of Authorisation", copy: "Nudges partners to procure the ₹41,680 certificate.",                     triggers: "Manual campaign" },
          { id: "nudge",               accent: "#2FE4C6", title: "F · Nudge",                              copy: "Generic gentle reminder — KYC, cart, dormant partners.",                    triggers: "Manual campaign" },
          { id: "festival",            accent: "#D8B23E", title: "G · Festival Wishing",                   copy: "Seasonal greetings (Diwali, Holi, Eid, etc.)",                              triggers: "Manual campaign" },
          { id: "special_offer",       accent: "#D8B23E", title: "H · Special Offer",                      copy: "Occasion-linked members-only promo.",                                        triggers: "Manual campaign" },
          { id: "achievement_unlocked",accent: "#D8B23E", title: "I · Achievement Unlocked",               copy: "Celebratory first-milestone email. Fires once per user.",                    triggers: "Post-KYC + first-login (existing verified)" },
          { id: "invitation",          accent: "#D8B23E", title: "J · Invitation · 5151 Series",           copy: "Formal cohort invite for a nominated buyer.",                                triggers: "Admin custom send" },
          { id: "agreement_nudge",     accent: "#E5484D", title: "K · Agreement Nudge",                    copy: "Firm reminder for pending e-signature.",                                     triggers: "Admin custom send · cron reminders" },
          { id: "market_alert",        accent: "#2FE4C6", title: "L · Market Alert · Rates Bulletin",       copy: "Manual dispatch of monthly bulletin to VIP clients.",                        triggers: "Admin custom send" },
          { id: "settlement_notice",   accent: "#2FE4C6", title: "M · Settlement Notice",                   copy: "Post-payout supplier statement.",                                            triggers: "Admin custom send · post-payout hook" },
          { id: "event_invite",        accent: "#2FE4C6", title: "N · Event Invite · Jitsi",                copy: "Webinar / Virtual Trade Desk session invite.",                               triggers: "Admin custom send" },
        ].map((t) => (
          <div key={t.id} data-testid={`communications-template-${t.id}`}
               className="border border-white/10 rounded-sm bg-[#0a0a0a] p-5">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.22em]" style={{ color: t.accent }}>Elite template</div>
            <div className="font-serif text-lg text-[#F5F5F0] mt-1">{t.title}</div>
            <div className="font-body text-xs text-[#8A8A82] mt-2 leading-relaxed">{t.copy}</div>
            <div className="border-t border-white/10 mt-3 pt-3 font-body text-[11px] text-[#8A8A82]">
              <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#6f6f68] block mb-1">Triggers</span>
              {t.triggers}
            </div>
          </div>
        ))}
      </div>

      {/* Test send */}
      <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-5">
        <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#B87333] mb-3 inline-flex items-center gap-1">
          <Eye size={11}/> Preview a live send
        </div>
        <div className="font-body text-xs text-[#8A8A82] mb-3">
          Sends the chosen template to any inbox. Bypasses the master switch (forced send) so
          you can QA even while automated mail is paused.
        </div>
        <div className="grid sm:grid-cols-3 gap-2">
          <select data-testid="communications-test-template" value={testTemplate} onChange={(e) => setTestTemplate(e.target.value)}
                  className="bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]">
            <option value="welcome">A · Welcome</option>
            <option value="trade_ack">B · Trade Executed</option>
            <option value="sponsorship_glory">C · Sponsorship Glory</option>
            <option value="ekyc_success">D · eKYC Verified</option>
            <option value="certificate_procure">E · Certificate Procure</option>
            <option value="nudge">F · Nudge</option>
            <option value="festival">G · Festival Wishing</option>
            <option value="special_offer">H · Special Offer</option>
            <option value="achievement_unlocked">I · Achievement Unlocked</option>
            <option value="invitation">J · Invitation · 5151 Series</option>
            <option value="agreement_nudge">K · Agreement Nudge</option>
            <option value="market_alert">L · Market Alert</option>
            <option value="settlement_notice">M · Settlement Notice</option>
            <option value="event_invite">N · Event Invite</option>
          </select>
          <input data-testid="communications-test-email" type="email" value={testEmail}
                 onChange={(e) => setTestEmail(e.target.value)} placeholder="your@inbox.com"
                 className="sm:col-span-1 bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
          <button data-testid="communications-send-test" onClick={sendTest} disabled={sending}
                  className="bg-[#B87333] hover:bg-[#A05F26] text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-4 py-2 rounded-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {sending ? <Loader2 size={12} className="animate-spin"/> : <Send size={12}/>}
            Send preview
          </button>
        </div>
      </div>

      {/* Verified-Sender Badge · SPF / DKIM / DMARC */}
      <SenderVerificationBadge api={api} />

      {/* Executive Stationery Suite · Send Custom Email */}
      <AdminCustomSendTool api={api} />

      <div className="border border-white/[0.04] rounded-sm bg-black/40 p-4 flex items-start gap-2">
        <Shield size={14} className="text-[#8A8A82] shrink-0 mt-0.5" />
        <div className="font-body text-[11px] text-[#8A8A82] leading-relaxed">
          <b className="text-[#C9C9C2]">Critical delivery guarantee:</b> password reset,
          one-time codes, and any flow calling <code className="text-[#B87333]">send_email(...,&nbsp;force=True)</code>
          will always deliver, even when the master switch is OFF.
        </div>
      </div>
    </div>
  );
}
